/** Shared customer top bar (fixed shell chrome) — typography and spacing for Journey + retail shell. */

/** C8 shell — live max-w-xl (36rem) via `--vcl-content-max-shell`. */
export const CUSTOMER_CONTENT_WIDTH_CLASS =
  'mx-auto w-full max-w-[var(--vcl-content-max-shell)]';

/**
 * Cookie consent banner card — fluid on small screens (`w-full`), capped on large
 * viewports at the shell budget (`--vcl-content-max-shell` / 36rem), centered.
 * Do not use `50vw` (too narrow on phones).
 */
export const CUSTOMER_COOKIE_BANNER_WIDTH_CLASS =
  'mx-auto w-full max-w-[var(--vcl-content-max-shell)]';

/**
 * Inline shell notices (consent sync failed, etc.) — same shell budget as the
 * cookie card, left-aligned in the main pane (no `mx-auto`).
 */
export const CUSTOMER_INLINE_NOTICE_WIDTH_CLASS =
  'w-full max-w-[var(--vcl-content-max-shell)]';

/**
 * Desktop shell outer frame — no max-width cap; sidebar + main fill viewport.
 * Used by CustomerAppShell outer wrapper on lg+.
 */
export const CUSTOMER_SHELL_WIDTH_CLASS = 'mx-auto w-full';

/**
 * Floating chrome dialogs (Profile / Settings / hub empty cart).
 * Fixed shared footprint so every end-icon panel looks the same (28rem).
 * No `mx-auto` — parent rail end-aligns under Org · Cart · Settings · Profile.
 * Shared Dialog / customer BottomSheet use max-w-lg for fuller sheets.
 */
export const CUSTOMER_CHROME_PANEL_WIDTH_CLASS = 'w-full max-w-md';

/**
 * ChromeTopPanel height budget — clears fixed shell header (safe-area + 3.5rem + mt-1)
 * and bottom chrome (`--customer-toast-offset` = BottomNav + sticky cart).
 * Max only: panels stay content-sized (no min-h stretch).
 */
export const CUSTOMER_CHROME_PANEL_MAX_HEIGHT_CLASS =
  'max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-top,0px)-3.75rem-var(--customer-toast-offset,0px)-0.75rem))]';

/**
 * Chrome panel overflow — tiny-viewport / tall-content safety (Profile, multi-cart).
 * Not a forced scroll region: on normal phone heights chrome content fits inside max-h.
 */
export const CUSTOMER_CHROME_PANEL_OVERFLOW_CLASS = 'overflow-y-auto';

/**
 * @deprecated Settings is content-sized (no min-h fill). Kept empty so any stale
 * import does not reintroduce viewport-tall blank space under Install & Update.
 */
export const CUSTOMER_SETTINGS_CHROME_PANEL_CLASS = '';

/**
 * Horizontal rail matching shell header content width (`CUSTOMER_ACCOUNT_HEADER_INNER_CLASS`
 * without the header row chrome). `justify-end` parks chrome panels under the end-icon cluster
 * on every viewport — not viewport-centered (especially visible on lg+ / desktop).
 */
export const CUSTOMER_CHROME_PANEL_RAIL_CLASS =
  'mx-auto flex w-full max-w-[var(--vcl-content-max-shell)] justify-end lg:max-w-none lg:pl-56';
/**
 * Narrow content — checkout, QR, sensitive inputs (live max-w-2xl / 42rem).
 * Cap via `--vcl-content-max-narrow` (C8); `min-w-0` keeps flex overflow chains.
 * Sign-in / register family uses `CUSTOMER_CONTENT_AUTH_CLASS` (tighter lg+ cap).
 */
export const CUSTOMER_CONTENT_NARROW_CLASS =
  'mx-auto w-full min-w-0 max-w-[var(--vcl-content-max-narrow)]';

/**
 * Auth card column (sign-in / register family) — C8 content-max via
 * `--vcl-content-max-auth` (31.416rem). Mobile stays fluid (`w-full`).
 * NOT the field-stack role (`CUSTOMER_AUTH_FIELD_CLASS`).
 */
export const CUSTOMER_CONTENT_AUTH_CLASS =
  'mx-auto w-full max-w-[var(--vcl-content-max-auth)]';

/**
 * Auth primary CTAs (Continue with Google / Sign in with password) — 20% under the
 * customer Button recipe cap (`max-w-sm` / 24rem → 19.2rem). Pass via `className` so
 * tailwind-merge replaces `max-w-sm`; keeps `block` + `mx-auto` centering.
 * C4 geometry hardcode (not C8 content-max).
 */
export const CUSTOMER_AUTH_PRIMARY_CTA_MAX_CLASS =
  'max-w-[19.2rem] whitespace-nowrap';
/**
 * Sign-in Password / OTP method toggle — `w-fit` + `grid-cols-2` sizes both
 * columns to the wider label and centers the pair (`mx-auto`). `max-w-full`
 * caps on narrow viewports. Children: `w-full` only — never Button `block` /
 * `min-w-full` (those stretch the grid to the card and left-align unequal cells).
 */
export const CUSTOMER_AUTH_METHOD_TOGGLE_CLASS =
  'mx-auto mb-4 grid w-fit max-w-full grid-cols-2 gap-2';

/**
 * Sign-in FormField stack (identifier / password / OTP) — separate C8 role
 * (`--vcl-auth-field-stack-width` / 86.25%). MUST NOT use `--vcl-content-max-auth`.
 */
export const CUSTOMER_AUTH_FIELD_CLASS =
  'mx-auto w-[var(--vcl-auth-field-stack-width)] max-w-full';
/**
 * Standard content — orders, account, receipts, settings (~900–1100px).
 */
export const CUSTOMER_CONTENT_STANDARD_CLASS =
  'mx-auto w-full max-w-[var(--vcl-content-max-standard)]';

/**
 * Wide content — shop catalog, directory, map, organizations (~1200–1440px).
 */
export const CUSTOMER_CONTENT_WIDE_CLASS =
  'mx-auto w-full max-w-[var(--vcl-content-max-wide)]';

/**
 * Full content — split-view / map surfaces; constrained only by shell padding.
 */
export const CUSTOMER_CONTENT_FULL_CLASS = 'mx-auto w-full';

/**
 * Header inner on lg+ — spans full main-pane width (no shell content-max cap).
 * Mobile keeps CUSTOMER_TOP_BAR_INNER_CLASS (inherits shell max from CUSTOMER_CONTENT_WIDTH_CLASS).
 */
/** @deprecated — absorbed into CUSTOMER_ACCOUNT_HEADER_INNER_CLASS */
export const CUSTOMER_TOP_BAR_INNER_DESKTOP_CLASS =
  'mx-auto flex w-full min-w-0 items-center gap-2 py-1.5 sm:gap-3 lg:max-w-none';

/**
 * Shared main-column content stack under CustomerAppShell for every tab,
 * account page, and JourneyShell route (Scan, etc.).
 * `py-4` (1rem) matches `.customer-safe-area-x` horizontal inset so page
 * headings sit equally from the top and left of the content box — same as
 * `DIRECTORY_PAGE_STACK_CLASS` `pt-4`. `gap-3` matches directory stacks.
 *
 * Title ownership: either JourneyShell/page chrome OR the child screen may
 * render the visible h1 — never both (avoids duplicate “Přihlášení” etc.).
 */
export const CUSTOMER_TAB_CONTENT_CLASS =
  'flex w-full min-w-0 flex-col gap-3 py-4';

/**
 * Fixed shell chrome header — L/R + top safe-area (notch) via customer.extensions.css.
 * `fixed inset-x-0 top-0 z-[60]` pins the bar to the viewport so
 * ← · Org · Cart · Settings · Profile (center title/place) stay above feature
 * overlays (Collect / QR / sheets at z-50).
 *
 * SHELL vs IN_DIALOG vs FLOW_SPACER:
 * - SHELL (`CUSTOMER_TOP_BAR_SHELL_HEADER_CLASS`): app-shell chrome — fixed + backdrop-blur.
 * - IN_DIALOG (`CUSTOMER_TOP_BAR_IN_DIALOG_HEADER_CLASS`): header inside a dialog/sheet — in-flow, no fixed/z.
 * - FLOW_SPACER (`CUSTOMER_TOP_BAR_FLOW_SPACER_CLASS`): in-flow height reserve under fixed SHELL chrome.
 *
 * Opaque `bg-[var(--color-surface)]` (no `supports-[backdrop-filter]:…/95` overlay).
 */
export const CUSTOMER_TOP_BAR_SHELL_HEADER_CLASS =
  'customer-safe-area-x customer-safe-area-top fixed inset-x-0 top-0 z-[60] border-b border-[var(--color-border)] bg-[var(--color-surface)] backdrop-blur';

/**
 * In-dialog / sheet header — same safe-area + border + opaque surface as SHELL,
 * without `fixed` / `z-[60]` so the bar stays in document flow inside the overlay.
 */
export const CUSTOMER_TOP_BAR_IN_DIALOG_HEADER_CLASS =
  'customer-safe-area-x customer-safe-area-top border-b border-[var(--color-border)] bg-[var(--color-surface)]';

/** @deprecated Use `CUSTOMER_TOP_BAR_SHELL_HEADER_CLASS`. */
export const CUSTOMER_TOP_BAR_HEADER_CLASS = CUSTOMER_TOP_BAR_SHELL_HEADER_CLASS;

/**
 * In-flow spacer matching fixed SHELL chrome height (safe-area + 3.5rem content row).
 * Place immediately under the shell so content is not hidden behind the `fixed` header.
 */
export const CUSTOMER_TOP_BAR_FLOW_SPACER_CLASS =
  'h-[calc(env(safe-area-inset-top,0px)+3.5rem)] shrink-0';

/** Content row ~56px (py-1.5 + h-11 icons); safe-area stays on SHELL_HEADER_CLASS. */
export const CUSTOMER_TOP_BAR_INNER_CLASS =
  `${CUSTOMER_CONTENT_WIDTH_CLASS} flex w-full min-w-0 items-center gap-2 py-1.5 sm:gap-3 lg:max-w-none`;

/**
 * Fixed-shell clearance under the content row (56px / 3.5rem) + safe-area.
 * Use for panel padding-top / sticky offsets that clear the header chrome.
 */
export const CUSTOMER_TOP_BAR_CLEARANCE_PT_CLASS =
  'pt-[calc(env(safe-area-inset-top,0px)+3.5rem)]';

export const CUSTOMER_TOP_BAR_CLEARANCE_TOP_CLASS =
  'top-[calc(env(safe-area-inset-top,0px)+3.5rem)]';

export const CUSTOMER_TOP_BAR_TITLE_CLASS =
  'truncate text-base font-semibold leading-snug text-[var(--color-on-surface)]';

export const CUSTOMER_TOP_BAR_SUBTITLE_CLASS =
  'truncate text-sm leading-snug text-[var(--color-on-surface-muted)]';

export const CUSTOMER_TOP_BAR_ACTIONS_CLASS =
  'flex min-w-0 shrink items-center justify-end gap-1.5 sm:gap-2';

/** Auth controls (sign-in pill, account avatar) — match language toggle scale. */
export const CUSTOMER_TOP_BAR_AUTH_LINK_CLASS =
  'inline-flex h-9 min-w-0 shrink items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3.5 text-sm font-medium leading-none text-[var(--color-on-surface)] hover:bg-[var(--color-surface)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]';

/**
 * Shell header row — chrome (~56px content row; safe-area on SHELL_HEADER_CLASS):
 * [← Back] · title/place · [Org] [Cart] [Settings] [Profile]
 * HUX-01 root `/browse`: no Back/title/Org; cart hide-empty; Settings/Profile → More.
 * (Prefs on Settings sheet — not Profile.)
 */
export const CUSTOMER_ACCOUNT_HEADER_INNER_CLASS =
  `${CUSTOMER_CONTENT_WIDTH_CLASS} flex w-full min-w-0 items-center justify-between gap-2 sm:gap-3 py-1.5 lg:max-w-none lg:pl-56`;

/** Legacy equal-column slot (tests / overlays that still center a widget). */
export const CUSTOMER_ACCOUNT_HEADER_SLOT_CLASS =
  'flex min-w-0 items-center justify-center';

/** ← Back rail — start of the header row. */
export const CUSTOMER_ACCOUNT_HEADER_START_SLOT_CLASS =
  'flex shrink-0 items-center justify-start';

/** Legacy center slot — kept for callers/tests that still import the token. */
export const CUSTOMER_ACCOUNT_HEADER_CENTER_SLOT_CLASS =
  'flex min-w-0 flex-1 items-center justify-center gap-1.5 sm:gap-2';

/**
 * Org · Cart · Settings · Profile — end cluster (right-aligned, shrink-0).
 * Do **not** use `overflow-hidden` here — it clipped Org/Settings at ≤430px when the
 * cluster fought the center `flex-1` title. Title truncation owns yield (`CENTER` min-w-0).
 * Horizontal clip lives on shell `main`, not the outer wrapper around the fixed header.
 *
 * End icons: Org · Cart · Settings · Profile.
 * Language/appearance/guest cookies live on Settings sheet; signed-in cookies under Account & privacy.
 */
export const CUSTOMER_ACCOUNT_HEADER_END_SLOT_CLASS =
  'flex shrink-0 items-center justify-end gap-0.5 sm:gap-1';

/** Org · Cart · Settings · Profile — never shrink/clip. */
export const CUSTOMER_ACCOUNT_HEADER_END_ICONS_CLASS =
  'flex shrink-0 items-center gap-0.5 sm:gap-1';

/**
 * Icon-only shell chrome control (cart / settings / profile) — min 44×44 touch target.
 */
export const CUSTOMER_SHELL_ICON_BUTTON_CLASS =
  'inline-flex h-11 min-h-11 w-11 min-w-11 shrink-0 items-center justify-center rounded-full text-[var(--color-on-surface)] hover:bg-[var(--color-surface-elevated)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-60';

/**
 * Organization control — Building + (sm+) label + chevron; shrink-0 so icons stay painted.
 * Narrow: label hidden via `hidden sm:inline` on the truncate span (icon-only below sm).
 * sm+: `sm:min-w-0 sm:max-w-[12rem]` lets the label truncate without collapsing icons.
 */
export const CUSTOMER_SHELL_ORG_CONTROL_CLASS =
  'inline-flex h-11 min-h-11 shrink-0 items-center gap-1.5 rounded-full px-2 text-sm font-medium text-[var(--color-on-surface)] hover:bg-[var(--color-surface-elevated)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] sm:min-w-0 sm:max-w-[12rem]';

/** Sign-out chip — same pill scale as sign-in (picture parity). */
export const CUSTOMER_ACCOUNT_HEADER_SIGN_OUT_CLASS = [
  CUSTOMER_TOP_BAR_AUTH_LINK_CLASS,
  'max-w-full overflow-hidden',
].join(' ');

/** Icon-only theme switch track (iOS-style wheel). */
export const CUSTOMER_ACCOUNT_HEADER_THEME_SWITCH_CLASS =
  'relative inline-flex h-8 w-12 shrink-0 items-center rounded-full border px-0.5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] sm:h-9 sm:w-14';

export const CUSTOMER_ACCOUNT_HEADER_THEME_SWITCH_THUMB_CLASS =
  'pointer-events-none inline-block h-6 w-6 rounded-full shadow transition-transform sm:h-7 sm:w-7';

export const CUSTOMER_TOP_BAR_AUTH_AVATAR_CLASS =
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface-elevated)] text-sm font-semibold leading-none text-[var(--color-on-surface)] hover:bg-[var(--color-surface)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]';
